import { API_BASE_URL } from '../types';
import { auth } from '../utils/firebase';

/**
 * Get the current user's ID token for API authentication
 */
async function getIdToken(): Promise<string | null> {
  try {
    const user = auth.currentUser;
    if (!user) {
      return null;
    }
    return await user.getIdToken();
  } catch (error) {
    console.error('Failed to get ID token:', error);
    return null;
  }
}

/**
 * Convert file using the backend API
 */
export async function convertFileWithAPI(
  file: File,
  bankId?: string,
  targetCurrency?: string,
  startDate?: string,
  enableCategorization?: boolean,
  budgetConfig?: {
    serverURL: string;
    password: string;
    budgetId: string;
    budgetPassword?: string;
  }
): Promise<string> {
  try {
    // Convert file to base64
    const fileData = await fileToBase64(file);
    
    // Prepare request body - only include bankId if provided
    const requestBody: any = {
      fileData,
      targetCurrency,
      startDate,
      enableCategorization,
      budgetConfig
    };
    
    if (bankId) {
      requestBody.bankId = bankId;
    }
    
    // Prepare headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    // Add authentication token if categorization is enabled
    if (enableCategorization) {
      const token = await getIdToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }
    
    const response = await fetch(`${API_BASE_URL}/convert-file`, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch {
        // If we can't parse the error response, use the status
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Conversion failed');
    }

    console.log(`✅ File converted successfully: ${result.transactionCount} transactions`);
    return result.csvContent;
  } catch (error) {
    console.error('❌ API conversion failed:', error);
    throw error;
  }
}

/**
 * Extended version that returns full API response for auto-detection
 */
export async function convertFileWithAPIExtended(
  file: File,
  bankId?: string,
  targetCurrency?: string,
  startDate?: string,
  enableCategorization?: boolean,
  budgetConfig?: {
    serverURL: string;
    password: string;
    budgetId: string;
    budgetPassword?: string;
  }
): Promise<{ csvContent: string; bankName?: string; inferredConfig?: any }> {
  try {
    // Convert file to base64
    const fileData = await fileToBase64(file);
    
    // Prepare request body - only include bankId if provided
    const requestBody: any = {
      fileData,
      targetCurrency,
      startDate,
      enableCategorization,
      budgetConfig
    };
    
    if (bankId) {
      requestBody.bankId = bankId;
    }
    
    // Prepare headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    // Add authentication token if categorization is enabled
    if (enableCategorization) {
      const token = await getIdToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }
    
    const response = await fetch(`${API_BASE_URL}/convert-file`, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch {
        // If we can't parse the error response, use the status
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Conversion failed');
    }

    console.log(`✅ File converted successfully: ${result.transactionCount} transactions`);
    return {
      csvContent: result.csvContent,
      bankName: result.bankName,
      inferredConfig: result.inferredConfig
    };
  } catch (error) {
    console.error('❌ API conversion failed:', error);
    throw error;
  }
}

/**
 * Convert file to base64 string
 */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the data:mime/type;base64, prefix
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
} 